import { useEffect, useRef } from 'react';
import { readBuilderMessage, THEME_EDITOR_CHANNEL, type BuilderToEditorDto, type EditorToBuilderDto } from '@xshop/shared';
import { builderOrigin } from './editorVisit';

// The one wire between a builder preview and the builder that opened it. Both directions live here, so
// the origin is checked in a single place and a payload is parsed in a single place: a message from
// another window is untrusted input, exactly like a request body at the HTTP edge.

/** Distributes over the union instead of collapsing it, so each message keeps the fields it alone carries. */
type WithoutEnvelope<T> = T extends { type: string } ? Omit<T, 'channel' | 'from'> : never;

/** One message the canvas may send up, named exactly as the shared contract names it, minus the envelope. */
export type BuilderPayload = WithoutEnvelope<EditorToBuilderDto>;

/** What a message from the builder is handed to, already parsed and already checked for its origin. */
export type BuilderHandler = (message: BuilderToEditorDto) => void;

// Sends one message up to the builder, addressed to the window that opened this preview.
export const postToBuilder = (payload: BuilderPayload): void => {
  window.parent.postMessage({ channel: THEME_EDITOR_CHANNEL, from: 'storefront', ...payload }, builderOrigin());
};

// Hands every message the builder sends to one handler, ignoring other frames and anything unparseable.
export function useBuilderChannel(active: boolean, onMessage: BuilderHandler): void {
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    if (!active) return undefined;
    const origin = builderOrigin();
    const listener = (event: MessageEvent) => {
      if (origin !== '*' && event.origin !== origin) return;
      const message = readBuilderMessage(event.data);
      if (message) handler.current(message);
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [active]);
}
